import { supabase } from '../supabaseClient';
import { buildPlanValidationContent, sha256Hex } from './journeyState';
import { buildPhaseTransitionSafetySnapshot } from './riskProfileFlags';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SHA256_PATTERN = /^[0-9a-f]{64}$/;
const DELIVERY_MODES = new Set(['app', 'document', 'app_and_document']);
const DOCUMENT_CHANNELS = new Set(['email', 'hand_delivery', 'post']);
const RUN_STATUSES = new Set(['ready', 'partially_proven', 'finalized', 'cancelled']);
const PROOF_TYPES = new Set(['app', 'document']);

export class PhaseTransitionError extends Error {
  constructor(message, code = 'phase_transition_failed') {
    super(message);
    this.name = 'PhaseTransitionError';
    this.code = code;
  }
}

function requiredString(value, code) {
  const normalized = typeof value === 'string' ? value.trim() : '';
  if (!normalized) throw new PhaseTransitionError('Transition de phase incomplète.', code);
  return normalized;
}

function requiredUuid(value, code) {
  const normalized = requiredString(value, code);
  if (!UUID_PATTERN.test(normalized)) {
    throw new PhaseTransitionError('Identifiant de transition invalide.', code);
  }
  return normalized;
}

function nullableTimestamp(value, code) {
  if (value === null || value === undefined) return null;
  const normalized = requiredString(value, code);
  if (!Number.isFinite(Date.parse(normalized))) {
    throw new PhaseTransitionError('Horodatage de transition invalide.', code);
  }
  return normalized;
}

function normalizeRun(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new PhaseTransitionError('Réponse de transition invalide.', 'invalid_transition_response');
  }

  const deliveryMode = requiredString(raw.delivery_mode, 'invalid_delivery_mode');
  const status = requiredString(raw.status, 'invalid_transition_status');
  const contentSha256 = requiredString(raw.content_sha256, 'invalid_content_hash');
  const proposedProtocolPhases = raw.proposed_protocol_phases;
  const documentChannel = raw.document_channel ?? null;
  const appArtifactSha256 = raw.app_artifact_sha256 ?? null;
  const appPlanId = raw.app_plan_id ?? null;

  if (!DELIVERY_MODES.has(deliveryMode) || !RUN_STATUSES.has(status)) {
    throw new PhaseTransitionError('État de transition invalide.', 'invalid_transition_state');
  }
  if (!SHA256_PATTERN.test(contentSha256)) {
    throw new PhaseTransitionError('Empreinte du programme invalide.', 'invalid_content_hash');
  }
  if (deliveryMode.includes('document') && !DOCUMENT_CHANNELS.has(documentChannel)) {
    throw new PhaseTransitionError('Canal de remise du document invalide.', 'invalid_document_channel');
  }
  if (deliveryMode === 'app' && documentChannel !== null) {
    throw new PhaseTransitionError('Canal document inattendu.', 'invalid_document_channel');
  }
  if (appArtifactSha256 !== null && !SHA256_PATTERN.test(String(appArtifactSha256))) {
    throw new PhaseTransitionError('Preuve de publication invalide.', 'invalid_app_artifact');
  }
  if (appPlanId !== null && (typeof appPlanId !== 'string' || !appPlanId.trim())) {
    throw new PhaseTransitionError('Identifiant du brouillon app invalide.', 'invalid_app_plan_id');
  }
  if (!proposedProtocolPhases || typeof proposedProtocolPhases !== 'object'
      || Array.isArray(proposedProtocolPhases)
      || !Array.isArray(proposedProtocolPhases.phases)) {
    throw new PhaseTransitionError('Parcours de phase proposé invalide.', 'invalid_phase_snapshot');
  }

  return Object.freeze({
    id: requiredUuid(raw.id, 'invalid_transition_id'),
    clientId: requiredUuid(raw.client_id, 'invalid_client_id'),
    sourceConsultationId: requiredUuid(raw.source_consultation_id, 'invalid_source_consultation'),
    targetConsultationId: requiredUuid(raw.target_consultation_id, 'invalid_target_consultation'),
    fromPhaseId: requiredString(raw.from_phase_id, 'invalid_from_phase'),
    toPhaseId: requiredString(raw.to_phase_id, 'invalid_to_phase'),
    deliveryMode,
    documentChannel,
    status,
    contentSha256,
    appArtifactSha256,
    appPlanId: appPlanId === null ? null : appPlanId.trim(),
    proposedProtocolPhases: Object.freeze(proposedProtocolPhases),
    appPublishedAt: nullableTimestamp(raw.app_published_at, 'invalid_app_published_at'),
    appActivatedAt: nullableTimestamp(raw.app_activated_at, 'invalid_app_activated_at'),
    documentConfirmedAt: nullableTimestamp(raw.document_confirmed_at, 'invalid_document_confirmed_at'),
    finalizedAt: nullableTimestamp(raw.finalized_at, 'invalid_finalized_at'),
    notifiedAt: nullableTimestamp(raw.notified_at, 'invalid_notified_at'),
  });
}

function rpcError(error, fallback) {
  const code = typeof error?.code === 'string' ? error.code : 'phase_transition_failed';
  if (code === '23505') return new PhaseTransitionError('Une autre transition est déjà en cours.', code);
  if (code === '23514') return new PhaseTransitionError('Le programme ou la phase a changé. Recharge la fiche avant de continuer.', code);
  if (code === '42501') return new PhaseTransitionError('Transition inaccessible pour cette cliente.', code);
  return new PhaseTransitionError(fallback, code);
}

export async function preparePhaseTransition({
  clientId,
  sourceConsultationId,
  targetConsultation,
  fromPhaseId,
  toPhaseId,
  deliveryMode,
  documentChannel = null,
  clinicalConfirmed = false,
  safetySnapshot,
} = {}) {
  const normalizedClientId = requiredUuid(clientId, 'invalid_client_id');
  const normalizedSourceId = requiredUuid(sourceConsultationId, 'invalid_source_consultation');
  const targetConsultationId = requiredUuid(targetConsultation?.id, 'invalid_target_consultation');
  const normalizedFrom = requiredString(fromPhaseId, 'invalid_from_phase');
  const normalizedTo = requiredString(toPhaseId, 'invalid_to_phase');
  const normalizedMode = requiredString(deliveryMode, 'invalid_delivery_mode');
  const normalizedChannel = documentChannel === null ? null : requiredString(documentChannel, 'invalid_document_channel');
  if (!DELIVERY_MODES.has(normalizedMode)) {
    throw new PhaseTransitionError('Mode de remise invalide.', 'invalid_delivery_mode');
  }
  if (normalizedMode.includes('document') && !DOCUMENT_CHANNELS.has(normalizedChannel)) {
    throw new PhaseTransitionError('Choisis comment le document sera remis.', 'invalid_document_channel');
  }
  if (normalizedMode === 'app' && normalizedChannel !== null) {
    throw new PhaseTransitionError('Canal document inattendu.', 'invalid_document_channel');
  }
  if (normalizedFrom === normalizedTo) {
    throw new PhaseTransitionError('La phase suivante doit être distincte.', 'invalid_phase_sequence');
  }
  if (clinicalConfirmed !== true) {
    throw new PhaseTransitionError('La validation clinique explicite d’Anissa est requise.', 'clinical_confirmation_required');
  }

  let normalizedSafetySnapshot;
  try {
    normalizedSafetySnapshot = buildPhaseTransitionSafetySnapshot(safetySnapshot);
  } catch (error) {
    throw new PhaseTransitionError(
      error?.message || 'Le contexte de sécurité structuré est incomplet.',
      'invalid_safety_snapshot',
    );
  }

  const planText = targetConsultation?.nutritionPlan
    || targetConsultation?.nutrition_plan
    || targetConsultation?.plan_text
    || '';
  if (!String(planText).trim()) {
    throw new PhaseTransitionError('La nouvelle version du programme est vide.', 'empty_target_plan');
  }
  const supplements = targetConsultation?.supplements || '';
  const contentSha256 = await sha256Hex(buildPlanValidationContent(planText, supplements));

  const { data, error } = await supabase.rpc('preparer_transition_phase_atomiquement', {
    p_client_id: normalizedClientId,
    p_source_consultation_id: normalizedSourceId,
    p_target_consultation_id: targetConsultationId,
    p_from_phase_id: normalizedFrom,
    p_to_phase_id: normalizedTo,
    p_delivery_mode: normalizedMode,
    p_document_channel: normalizedChannel,
    p_content_sha256: contentSha256,
    p_clinical_confirmed: true,
    p_safety_snapshot: normalizedSafetySnapshot,
  });
  if (error) throw rpcError(error, 'La préparation de la phase suivante a échoué.');
  return normalizeRun(data);
}

export async function getActivePhaseTransition(clientId) {
  const normalizedClientId = requiredUuid(clientId, 'invalid_client_id');
  const { data, error } = await supabase.rpc('obtenir_transition_phase_active', {
    p_client_id: normalizedClientId,
  });
  if (error) throw rpcError(error, 'Impossible de charger la transition en cours.');
  return data === null ? null : normalizeRun(data);
}

export async function getPendingPhaseTransitionNotification(clientId) {
  const normalizedClientId = requiredUuid(clientId, 'invalid_client_id');
  const { data, error } = await supabase.rpc('obtenir_transition_phase_notification_en_attente', {
    p_client_id: normalizedClientId,
  });
  if (error) throw rpcError(error, 'Impossible de charger la notification de phase en attente.');
  if (data === null) return null;
  const run = normalizeRun(data);
  if (run.status !== 'finalized'
      || run.notifiedAt !== null
      || run.appActivatedAt === null
      || !['app', 'app_and_document'].includes(run.deliveryMode)) {
    throw new PhaseTransitionError(
      'Notification de transition incohérente.',
      'invalid_pending_notification',
    );
  }
  return run;
}

export async function getPendingPhaseTransitionActivation(clientId) {
  const normalizedClientId = requiredUuid(clientId, 'invalid_client_id');
  const { data, error } = await supabase.rpc('obtenir_transition_phase_activation_en_attente', {
    p_client_id: normalizedClientId,
  });
  if (error) throw rpcError(error, 'Impossible de charger l’activation app en attente.');
  if (data === null) return null;
  const run = normalizeRun(data);
  if (run.status !== 'finalized'
      || run.appActivatedAt !== null
      || !run.appPlanId
      || !run.appArtifactSha256
      || !['app', 'app_and_document'].includes(run.deliveryMode)) {
    throw new PhaseTransitionError(
      'Activation app en attente incohérente.',
      'invalid_pending_activation',
    );
  }
  return run;
}

export async function finalizePhaseTransition({
  transitionId,
  proofType,
  proofSha256,
  appPlanId = null,
} = {}) {
  const normalizedTransitionId = requiredUuid(transitionId, 'invalid_transition_id');
  const normalizedProofType = requiredString(proofType, 'invalid_proof_type');
  const normalizedProof = requiredString(proofSha256, 'invalid_proof_hash');
  if (!PROOF_TYPES.has(normalizedProofType) || !SHA256_PATTERN.test(normalizedProof)) {
    throw new PhaseTransitionError('Preuve de remise invalide.', 'invalid_transition_proof');
  }
  const normalizedAppPlanId = appPlanId === null ? null : requiredString(appPlanId, 'invalid_app_plan_id');
  if ((normalizedProofType === 'app') !== (normalizedAppPlanId !== null)) {
    throw new PhaseTransitionError(
      'Le brouillon app exact est requis pour finaliser cette remise.',
      'invalid_app_plan_id',
    );
  }
  const { data, error } = await supabase.rpc('finaliser_transition_phase_atomiquement', {
    p_transition_id: normalizedTransitionId,
    p_proof_type: normalizedProofType,
    p_proof_sha256: normalizedProof,
    p_app_plan_id: normalizedAppPlanId,
  });
  if (error) throw rpcError(error, 'La transition n’a pas pu être finalisée.');
  return normalizeRun(data);
}

export async function markPhaseTransitionAppActivated(
  transitionId,
  appPlanId,
  artifactSha256,
) {
  const normalizedTransitionId = requiredUuid(transitionId, 'invalid_transition_id');
  const normalizedPlanId = requiredString(appPlanId, 'invalid_app_plan_id');
  const normalizedArtifact = requiredString(artifactSha256, 'invalid_app_artifact');
  if (!SHA256_PATTERN.test(normalizedArtifact)) {
    throw new PhaseTransitionError('Preuve d’activation app invalide.', 'invalid_app_artifact');
  }
  const { data, error } = await supabase.rpc('marquer_transition_phase_app_activee', {
    p_transition_id: normalizedTransitionId,
    p_plan_id: normalizedPlanId,
    p_artifact_sha256: normalizedArtifact,
  });
  if (error) throw rpcError(error, 'L’activation app n’a pas pu être journalisée.');
  return normalizeRun(data);
}

export async function markPhaseTransitionNotified(transitionId) {
  const normalizedTransitionId = requiredUuid(transitionId, 'invalid_transition_id');
  const { data, error } = await supabase.rpc('marquer_transition_phase_notifiee', {
    p_transition_id: normalizedTransitionId,
  });
  if (error) throw rpcError(error, 'La notification n’a pas pu être journalisée.');
  return normalizeRun(data);
}

export async function cancelPhaseTransition(transitionId) {
  const normalizedTransitionId = requiredUuid(transitionId, 'invalid_transition_id');
  const { data, error } = await supabase.rpc('annuler_transition_phase_atomiquement', {
    p_transition_id: normalizedTransitionId,
  });
  if (error) throw rpcError(error, 'La préparation ne peut pas être annulée.');
  return normalizeRun(data);
}

export function getPublicationConsultation(targetConsultation, run) {
  const normalizedRun = normalizeRun({
    id: run?.id,
    client_id: run?.clientId,
    source_consultation_id: run?.sourceConsultationId,
    target_consultation_id: run?.targetConsultationId,
    from_phase_id: run?.fromPhaseId,
    to_phase_id: run?.toPhaseId,
    delivery_mode: run?.deliveryMode,
    document_channel: run?.documentChannel,
    status: run?.status,
    content_sha256: run?.contentSha256,
    app_artifact_sha256: run?.appArtifactSha256,
    app_plan_id: run?.appPlanId,
    proposed_protocol_phases: run?.proposedProtocolPhases,
    app_published_at: run?.appPublishedAt,
    app_activated_at: run?.appActivatedAt,
    document_confirmed_at: run?.documentConfirmedAt,
    finalized_at: run?.finalizedAt,
    notified_at: run?.notifiedAt,
  });
  if (!targetConsultation || String(targetConsultation.id || '') !== normalizedRun.targetConsultationId) {
    throw new PhaseTransitionError('La version à publier ne correspond pas à la transition.', 'target_mismatch');
  }
  return {
    ...targetConsultation,
    protocol_phases: normalizedRun.proposedProtocolPhases,
    active_phase_id: normalizedRun.toPhaseId,
    phaseTransitionRun: normalizedRun,
  };
}
